import { Skeleton } from "@patient-watch/ui/skeleton";
import ErrorPage from "@/components/pages/ErrorPage";
import { DiaryEditDisabledProvider } from "@/features/diaries/context/DiaryEditDisabledContext";
import HistoryByTracker from "@/features/diaries/features/history/components/HistoryByTracker";
import { PreviewHeaderOrganisationSync } from "@/features/previews/context/PreviewHeaderContext";
import PatientCard from "@/features/patients/features/patient/PatientCard";
import { normalizePrintTokenFromSearch } from "@/features/previews/utils/normalizePrintTokenFromSearch";
import { useApplyProgrammeLocaleFromSearch } from "@/features/i18n/useApplyProgrammeLocaleFromSearch";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { resolveReportDataApiBase } from "@/features/previews/utils/resolveReportDataApiBase";
import { diaryByIdQueryOptions } from "@/features/diaries/api";
import { selectProfileByIdQueryOptions } from "@/features/profile/api/selectProfile";
import type { StatefulDiaryT } from "@patient-watch/types";
import type { ProfileT } from "@/types/database/profiles";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense, useEffect } from "react";
import { z } from "zod";
import { normaliseLocaleTag } from "@patient-watch/i18n";

const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

const previewSearchSchema = z.object({
    token: z.string().optional(),
});

const LoadingSkeleton = () => (
    <div className="space-y-4">
        <Skeleton className="w-full h-[200px]" />
        <Skeleton className="w-full h-[200px]" />
        <Skeleton className="w-full h-[400px]" />
        <Skeleton className="w-full h-[300px]" />
    </div>
);

function resolveDiaryHospitalNumber(diary: StatefulDiaryT): string | null {
    const fromSharedAttributes = diary.shared_attributes?.hospital_number?.trim();
    if (fromSharedAttributes) return fromSharedAttributes;

    const fromDiaryField = typeof diary.hospital_number === "string" ? diary.hospital_number.trim() : "";
    if (fromDiaryField) return fromDiaryField;

    return diary.hospital_numbers?.find((value) => value?.trim())?.trim() ?? null;
}

function DiaryReportLayout({ diary, profile }: { diary: StatefulDiaryT; profile: ProfileT }) {
    return (
        <>
            <PreviewHeaderOrganisationSync
                organisationId={diary.organisation_id as string}
                organisationName={diary.organisation_name}
            />
            <DiaryEditDisabledProvider disabled>
                <PatientCard
                    editable={false}
                    profile={profile}
                    showPatientIdentifiers
                    hospitalNumber={resolveDiaryHospitalNumber(diary)}
                    diaryStartDate={diary.start_date}
                    diaryEndDate={diary.end_date}
                />
                <HistoryByTracker diary={diary} printMode hideMissingDetailsCall expandEntryValuesByDefault />
                <div id="print-ready" data-testid="print-ready" aria-hidden="true" />
            </DiaryEditDisabledProvider>
        </>
    );
}

function TokenPreviewContent() {
    const loaderData = Route.useLoaderData();
    const { locale } = useAppLocale();
    const reportLocale = normaliseLocaleTag(loaderData?.profile?.preferred_locale);

    // The print page renders anonymously, so LocaleProvider has no session to read
    // and falls back to English. Render the PDF in the patient's own language.
    // ponytail: covers UI chrome only; questionnaire-content translation is
    // seeded in form_translations, which anon cannot read - that overlay has to
    // happen server-side in private-get-report-data (see issue notes).
    useApplyProgrammeLocaleFromSearch(loaderData?.profile?.preferred_locale);

    // Do not expose the print-ready marker until the requested locale has actually
    // propagated through the provider; Puppeteer renders as soon as it sees it.
    if (!loaderData?.diary || !loaderData?.profile || (reportLocale && locale !== reportLocale)) {
        return <LoadingSkeleton />;
    }

    return <DiaryReportLayout diary={loaderData.diary} profile={loaderData.profile} />;
}

function SessionPreviewContent() {
    const { patientId, diaryId } = Route.useParams();
    const diaryQuery = useSuspenseQuery({
        ...diaryByIdQueryOptions(diaryId),
        retry: true,
        retryDelay: (attemptIndex: number) => Math.min(500 * 2 ** attemptIndex, 10000),
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        refetchOnReconnect: true,
        staleTime: 0,
    });
    const profileQuery = useSuspenseQuery({
        ...selectProfileByIdQueryOptions(patientId),
        retry: true,
        retryDelay: (attemptIndex: number) => Math.min(500 * 2 ** attemptIndex, 10000),
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        refetchOnReconnect: true,
        staleTime: 0,
    });

    useEffect(() => {
        if (!diaryQuery.data) {
            void diaryQuery.refetch();
        }
        if (!profileQuery.data) {
            void profileQuery.refetch();
        }
    }, [diaryQuery, profileQuery]);

    if (!diaryQuery.data || !profileQuery.data) {
        return <LoadingSkeleton />;
    }

    return <DiaryReportLayout diary={diaryQuery.data} profile={profileQuery.data} />;
}

function DiaryPreviewPage() {
    const loaderData = Route.useLoaderData();

    if (loaderData?.fromToken) {
        return <TokenPreviewContent />;
    }

    return (
        <Suspense fallback={<LoadingSkeleton />}>
            <SessionPreviewContent />
        </Suspense>
    );
}

export const Route = createFileRoute("/preview/patient/$patientId/diary/$diaryId/")({
    component: DiaryPreviewPage,
    errorComponent: ({ error }) => (
        <>
            <div
                id="print-error"
                data-reason="preview-load-failed"
                data-message={(error as Error)?.message ?? "Preview failed"}
                aria-hidden="true"
            />
            <ErrorPage title="Preview Error" error={error as Error} reset={() => window.location.reload()} />
        </>
    ),
    validateSearch: (search) => previewSearchSchema.parse(search),
    loaderDeps: ({ search: { token } }) => ({ token }),
    loader: async ({ context, params, deps: { token } }) => {
        const { diaryId, patientId } = params as {
            diaryId: string;
            patientId: string;
        };

        if (token) {
            const printToken = normalizePrintTokenFromSearch(token);
            const apiBase = resolveReportDataApiBase();
            const res = await fetch(`${apiBase}/functions/v1/public-get-report-data`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
                },
                body: JSON.stringify({ token: printToken }),
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error((err?.error as string) ?? `Failed to load report data (${res.status})`);
            }

            const data = await res.json();
            if (!data?.diary || !data?.profile) {
                throw new Error("Invalid report data");
            }
            if (data.diary.patient_id !== patientId) {
                throw new Error("Patient mismatch");
            }
            if (data.diary.diary_id !== diaryId) {
                throw new Error("Diary mismatch");
            }

            return { diary: data.diary, profile: data.profile, fromToken: true as const };
        }

        try {
            await Promise.all([
                context?.queryClient.prefetchQuery(diaryByIdQueryOptions(diaryId)),
                context?.queryClient.prefetchQuery(selectProfileByIdQueryOptions(patientId)),
            ]);
        } catch (error) {
            console.error("Failed to prefetch data:", error);
        }

        return { fromToken: false as const };
    },
    pendingComponent: LoadingSkeleton,
});
